<template>
  <div class="form-section">
    <h3 class="form-title">{{ $t('ProcessAfterSale.processInfo') }}</h3>
    
    <el-form ref="processForm" :model="localFormData" label-width="120px" class="after-sale-form">
      <!-- 产品型号 -->
      <el-form-item :label="$t('ProcessAfterSale.productModel')">
        <el-input v-model="localFormData.productModel" :placeholder="$t('ProcessAfterSale.productModelPlaceholder')" :disabled="finalReadOnly"/>
      </el-form-item>
      
      <!-- 跟进处理方案 -->
      <el-form-item :label="$t('ProcessAfterSale.processingPlan')">
        <el-input
          v-model="localFormData.processingPlan"
          type="textarea"
          :placeholder="$t('ProcessAfterSale.processingPlanPlaceholder')"
          :rows="4"
          :disabled="finalReadOnly"
        />
      </el-form-item>
      
      <!-- 是否退换货 -->
      <el-form-item :label="$t('ProcessAfterSale.isReturnExchange')">
        <el-radio-group v-model="localFormData.isReturnExchange" :disabled="finalReadOnly">
          <el-radio label="true">{{ $t('ProcessAfterSale.yes') }}</el-radio>
          <el-radio label="false">{{ $t('ProcessAfterSale.no') }}</el-radio>
        </el-radio-group>
      </el-form-item>
      
      <!-- RMA单号（条件显示） -->
      <el-form-item v-if="localFormData.isReturnExchange === 'true'" :label="$t('ProcessAfterSale.rmaNumber')">
        <el-input v-model="localFormData.rmaNumber" :placeholder="$t('ProcessAfterSale.rmaNumberPlaceholder')" :disabled="finalReadOnly"/>
      </el-form-item>
      
      <!-- 是否已解决 -->
      <el-form-item :label="$t('ProcessAfterSale.isResolved')">
        <el-radio-group v-model="localFormData.isResolved" :disabled="finalReadOnly">
          <el-radio label="true">{{ $t('ProcessAfterSale.yes') }}</el-radio>
          <el-radio label="false">{{ $t('ProcessAfterSale.no') }}</el-radio>
        </el-radio-group>
      </el-form-item>
      
      <!-- 状态下拉框 -->
      <el-form-item :label="$t('ProcessAfterSale.status')">
        <el-select v-model="localFormData.status" :placeholder="$t('ProcessAfterSale.selectStatus')" :disabled="finalReadOnly">
          <el-option :label="$t('ProcessAfterSale.pending')" value="pending"/>
          <el-option :label="$t('ProcessAfterSale.processing')" value="processing"/>
          <el-option :label="$t('ProcessAfterSale.completed')" value="completed"/>
        </el-select>
      </el-form-item>
      
      <!-- 提交按钮：仅当非只读状态时显示 -->
      <el-form-item v-if="!finalReadOnly">
        <el-button type="primary" @click="submitForm">{{ $t('ProcessAfterSale.submit') }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { updateAfterSalesData } from '@/api/api';

export default {
  name: 'AfterSalesProcessForm',
  props: {
    // 只读状态属性
    readonly: {
      type: Boolean,
      default: false
    },
    // 表单数据属性，允许外部传入
    formData: {
      type: Object,
      default: () => ({
        id: null,
        productModel: '',
        processingPlan: '',
        isReturnExchange: 'false',
        rmaNumber: '',
        isResolved: 'false',
        status: 'pending'
      })
    }
  },
  data() {
    return {
      // 创建本地数据副本，避免直接修改props
      // 确保所有字段都有默认值，特别是布尔类型字段
      localFormData: {
        id: null,
        productModel: '',
        processingPlan: '',
        isReturnExchange: 'false',
        rmaNumber: '',
        isResolved: 'false',
        status: 'pending',
        ...this.formData
      }
    }
  },
  computed: {
    // 计算属性：判断当前是否为已完结状态
    isCompleted() {
      return this.localFormData.status === 'completed';
    },
    // 计算属性：最终的只读状态 - 仅由外部设置的readonly属性决定
    // 用户选择已完结状态时不会立即禁用表单，只有在回显时才变为只读
    finalReadOnly() {
      return this.readonly;
    }
  },
  watch: {
    // 监听formData变化，更新本地数据
    formData: {
      handler(newVal) {
        // 合并新值到本地数据，保留默认值结构
        this.localFormData = {
          id: null,
          productModel: '',
          processingPlan: '',
          isReturnExchange: 'false',
          rmaNumber: '',
          isResolved: 'false',
          status: 'pending',
          ...newVal
        };
      },
      deep: true
    }
  },
  methods: {
    // 提交表单方法
    submitForm() {
      
      // 尝试从路由参数获取售后ID，如果localFormData.id为空
      let afterSalesId = this.localFormData.id;
      if (!afterSalesId) {
        afterSalesId = this.$route.params.taskId;
      }
      
      // 验证必填字段
      if (!afterSalesId) {
        this.$message.error('缺少售后ID');
        return;
      }
      
      // 构建提交数据，根据后端接口参数进行适配
      // 使用URLSearchParams来适配x-www-form-urlencoded格式
      const formData = new URLSearchParams();
      formData.append('product_model', this.localFormData.productModel);
      formData.append('solution', this.localFormData.processingPlan);
      formData.append('is_return', this.localFormData.isReturnExchange === 'true' ? 1 : 0);
      formData.append('ram_code', this.localFormData.rmaNumber || '');
      formData.append('is_resolve', this.localFormData.isResolved === 'true' ? 1 : 0);
      formData.append('process_status', this.getProcessStatusValue(this.localFormData.status));
      
      // 调用API更新售后处理信息
      updateAfterSalesData(afterSalesId, formData)
        .then(response => {
          if (response.code === 0) {
            this.$message.success(this.$t('ProcessAfterSale.submitSuccess'));
            // 触发成功事件，通知父组件
            this.$emit('submit-success', response.data);
            // 跳转到服务中心首页
            this.$router.push('/ServiceCenter');
          } else {
            this.$message.error(response.message || this.$t('ProcessAfterSale.submitFailed'));
          }
        })
        .catch(error => {
          console.error('提交失败:', error);
          this.$message.error('网络错误，请稍后重试');
        });
    },
    
    // 将状态字符串转换为后端需要的数值
    getProcessStatusValue(status) {
      const statusMap = {
        'pending': 0,
        'processing': 1,
        'completed': 2
      };
      return statusMap[status] || 0;
    }
  }
}
</script>

<style scoped>
.form-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.form-title {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}

.after-sale-form {
  max-width: 600px;
}
</style>